/**
 * Grouped Product Selector
 * Handles dynamic product switching based on option selection
 */

class GroupedProductSelector {
  constructor() {
    console.log('[GroupedProduct] Initializing...');

    this.container = document.getElementById('grouped-product');
    if (!this.container) {
      console.log('[GroupedProduct] Container not found, exiting');
      return;
    }

    this.mapping = null;
    this.currentProduct = null;
    this.selectedOptions = {};
    this.bandType = null;
    this.skuPrefix = 'H'; // Default to Hudson

    // Load config and initialize
    this.loadConfig();
  }

  async loadConfig() {
    const configEl = document.getElementById('grouped-product-config');
    if (!configEl) {
      console.error('[GroupedProduct] Config element not found');
      this.showError('Configuration not found');
      return;
    }

    let config;
    try {
      config = JSON.parse(configEl.textContent);
      console.log('[GroupedProduct] Config loaded, mapping URL:', config.mappingUrl);
    } catch (e) {
      console.error('[GroupedProduct] Failed to parse config:', e);
      this.showError('Failed to parse configuration');
      return;
    }

    // Set current product and band type from config
    this.currentProduct = config.currentProduct;
    this.bandType = config.bandType || 'hudson';

    // Set SKU prefix based on band type
    const skuPrefixes = { hudson: 'H', everett: 'E', james: 'J' };
    this.skuPrefix = skuPrefixes[this.bandType] || 'H';

    console.log('[GroupedProduct] Band type:', this.bandType, 'SKU prefix:', this.skuPrefix);
    console.log('[GroupedProduct] Current product:', this.currentProduct.handle, 'SKU:', this.currentProduct.sku);

    // Fetch mapping file
    try {
      console.log('[GroupedProduct] Fetching mapping from:', config.mappingUrl);
      const response = await fetch(config.mappingUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      this.mapping = await response.json();
      console.log('[GroupedProduct] Mapping loaded, products:', Object.keys(this.mapping.products || {}).length);
    } catch (e) {
      console.error('[GroupedProduct] Failed to fetch mapping:', e);
      this.showError('Failed to load product options');
      return;
    }

    // Initialize the selector
    this.init();
  }

  showError(message) {
    const optionsContainer = document.getElementById('product-options');
    if (optionsContainer) {
      optionsContainer.innerHTML = `<div style="color: red;">${message}. Check console for details.</div>`;
    }
  }

  init() {
    // Parse current product SKU to get initial selected options
    this.parseCurrentProductOptions();

    // Render option selectors
    this.renderOptions();

    // Bind thumbnail clicks
    this.bindThumbnailClicks();

    // Bind form submission
    this.bindFormSubmit();

    // Bind ring size error clearing
    this.bindRingSizeValidation();
  }

  bindRingSizeValidation() {
    // Clear error when ring size is selected
    const ringSizeSelect = document.querySelector('#RingSize');
    const ringSizeRadios = document.querySelectorAll('input[name="properties[Ring Size]"]');
    const errorEl = document.getElementById('ring-size-error');

    if (ringSizeSelect && errorEl) {
      ringSizeSelect.addEventListener('change', () => {
        if (ringSizeSelect.value) {
          errorEl.style.display = 'none';
        }
      });
    }

    if (ringSizeRadios.length > 0 && errorEl) {
      ringSizeRadios.forEach(radio => {
        radio.addEventListener('change', () => {
          errorEl.style.display = 'none';
        });
      });
    }
  }

  parseCurrentProductOptions() {
    const sku = this.currentProduct.sku;

    // First, set defaults from the mapping for options with only one value
    // This ensures options like pave=NP for Everett are always set
    for (const [optionKey, option] of Object.entries(this.mapping.options)) {
      if (option.values.length === 1) {
        this.selectedOptions[optionKey] = option.values[0];
      }
    }

    if (!sku) {
      console.log('[GroupedProduct] No SKU, using defaults:', this.selectedOptions);
      return;
    }

    // Parse SKU to extract option values
    // Format: {prefix}-{width}-{metal}-{finish}-{pave}-{pave_color}-{gold_inlay}
    // Or: {prefix}--6.5-{metal}-{finish}-{pave}-{pave_color}-{gold_inlay} for 6.5mm

    const prefix = this.skuPrefix;
    let parts;

    // Handle special 6.5mm case with double hyphen
    if (sku.startsWith(`${prefix}--6.5-`)) {
      this.selectedOptions.width = '6.5';
      parts = sku.substring(prefix.length + 6).split('-'); // Skip "{prefix}--6.5-"
      if (parts.length >= 5) {
        this.selectedOptions.metal = parts[0];
        this.selectedOptions.finish = parts[1];
        this.selectedOptions.pave = parts[2];
        this.selectedOptions.pave_color = parts[3];
        this.selectedOptions.gold_inlay = parts[4];
      }
    } else {
      parts = sku.split('-');
      if (parts.length >= 7 && parts[0] === prefix) {
        this.selectedOptions.width = parts[1];
        this.selectedOptions.metal = parts[2];
        this.selectedOptions.finish = parts[3];
        this.selectedOptions.pave = parts[4];
        this.selectedOptions.pave_color = parts[5];
        this.selectedOptions.gold_inlay = parts[6];
      }
    }

    console.log('[GroupedProduct] Parsed options from SKU:', this.selectedOptions);
  }

  renderOptions() {
    const optionsContainer = document.getElementById('product-options');
    if (!optionsContainer) return;

    const optionOrder = this.mapping.optionOrder || Object.keys(this.mapping.options);

    // Filter out options with only one value (nothing for user to choose)
    const visibleOptions = optionOrder.filter(optionKey => {
      const option = this.mapping.options[optionKey];
      return option && option.values.length > 1;
    });

    // Set active tab to first visible option
    if (!this.activeTab || !visibleOptions.includes(this.activeTab)) {
      this.activeTab = visibleOptions[0];
    }

    // Build tabs HTML
    let tabsHtml = '<div class="grouped-product__tabs">';
    let panelsHtml = '<div class="grouped-product__tab-panels">';

    for (const optionKey of visibleOptions) {
      const option = this.mapping.options[optionKey];
      if (!option) continue;

      const isActiveTab = optionKey === this.activeTab;
      const selectedValue = this.selectedOptions[optionKey] || option.values[0];
      const hasSelection = this.selectedOptions[optionKey] !== undefined;

      // Tab button
      tabsHtml += `
        <button
          type="button"
          class="grouped-product__tab ${isActiveTab ? 'is-active' : ''} ${hasSelection ? 'has-selection' : ''}"
          data-tab="${optionKey}"
        >
          ${option.label}
        </button>
      `;

      // Tab panel with option cards
      panelsHtml += `
        <div class="grouped-product__tab-panel ${isActiveTab ? 'is-active' : ''}" data-panel="${optionKey}">
          <div class="grouped-product__option-cards">
      `;

      for (const value of option.values) {
        const valueLabel = option.displayLabels[value] || value;
        const isSelected = value === selectedValue;
        const isAvailable = this.isOptionAvailable(optionKey, value);

        panelsHtml += `
          <button
            type="button"
            class="grouped-product__option-card ${isSelected ? 'is-selected' : ''} ${!isAvailable ? 'is-unavailable' : ''}"
            data-option="${optionKey}"
            data-value="${value}"
            ${!isAvailable ? 'disabled' : ''}
          >
            <span class="grouped-product__option-card-label">${valueLabel}</span>
            <span class="grouped-product__option-card-info">i</span>
          </button>
        `;
      }

      panelsHtml += `
          </div>
        </div>
      `;
    }

    tabsHtml += '</div>';
    panelsHtml += '</div>';

    optionsContainer.innerHTML = tabsHtml + panelsHtml;

    // Bind tab click events
    optionsContainer.querySelectorAll('.grouped-product__tab').forEach(tab => {
      tab.addEventListener('click', (e) => this.onTabClick(e));
    });

    // Bind option card click events
    optionsContainer.querySelectorAll('.grouped-product__option-card').forEach(card => {
      card.addEventListener('click', (e) => this.onOptionClick(e));
    });
  }

  onTabClick(e) {
    const tab = e.currentTarget;
    const tabKey = tab.dataset.tab;

    this.activeTab = tabKey;

    // Update tab active states
    document.querySelectorAll('.grouped-product__tab').forEach(t => {
      t.classList.toggle('is-active', t.dataset.tab === tabKey);
    });

    // Update panel active states
    document.querySelectorAll('.grouped-product__tab-panel').forEach(p => {
      p.classList.toggle('is-active', p.dataset.panel === tabKey);
    });
  }

  isOptionAvailable(optionKey, value) {
    // Check if there's any product with this option value
    // For interdependent options (pave/pave_color), we need special handling

    const testOptions = { ...this.selectedOptions, [optionKey]: value };

    // Ensure all required options have values (use first available from mapping)
    for (const [key, option] of Object.entries(this.mapping.options)) {
      if (!testOptions[key] && option.values.length > 0) {
        testOptions[key] = option.values[0];
      }
    }

    // Handle pave/pave_color interdependency (only if these options exist with multiple values)
    const paveOption = this.mapping.options.pave;
    const paveColorOption = this.mapping.options.pave_color;

    if (paveOption && paveOption.values.length > 1) {
      if (optionKey === 'pave') {
        if (value === 'NP') {
          testOptions.pave_color = 'NP';
        } else if (testOptions.pave_color === 'NP' && paveColorOption && paveColorOption.values.includes('WM')) {
          testOptions.pave_color = 'WM';
        }
      }

      if (optionKey === 'pave_color') {
        if (value === 'NP') {
          testOptions.pave = 'NP';
        } else if (testOptions.pave === 'NP' && paveOption.values.includes('CP')) {
          testOptions.pave = 'CP';
        } else if (testOptions.pave === 'NP' && paveOption.values.includes('SP')) {
          testOptions.pave = 'SP';
        }
      }
    }

    return this.findProductHandle(testOptions) !== null;
  }

  findProductHandle(options) {
    // Build lookup key from options - ensure all parts exist
    const parts = [
      options.width || '',
      options.metal || '',
      options.finish || '',
      options.pave || '',
      options.pave_color || '',
      options.gold_inlay || ''
    ];

    // Check if any part is missing
    if (parts.some(p => !p)) {
      console.warn('[GroupedProduct] Missing option values:', options);
      return null;
    }

    const lookupKey = parts.join('-');
    return this.mapping.productMap[lookupKey] || null;
  }

  onOptionClick(e) {
    const btn = e.currentTarget;
    const optionKey = btn.dataset.option;
    const value = btn.dataset.value;

    // Update selected options
    this.selectedOptions[optionKey] = value;

    // Handle pave/pave_color interdependency (only if these options exist with multiple values)
    const paveOption = this.mapping.options.pave;
    const paveColorOption = this.mapping.options.pave_color;

    if (paveOption && paveOption.values.length > 1) {
      if (optionKey === 'pave') {
        if (value === 'NP') {
          // No pave means pave_color must be NP
          this.selectedOptions.pave_color = 'NP';
        } else if (this.selectedOptions.pave_color === 'NP') {
          // Switching to pave, need a valid pave_color - try WM first
          if (paveColorOption && paveColorOption.values.includes('WM')) {
            this.selectedOptions.pave_color = 'WM';
          }
        }
      }

      if (optionKey === 'pave_color') {
        if (value === 'NP') {
          // No color means pave must be NP
          this.selectedOptions.pave = 'NP';
        } else if (this.selectedOptions.pave === 'NP') {
          // Selecting a color, need pave - try CP first, then SP
          if (paveOption.values.includes('CP')) {
            this.selectedOptions.pave = 'CP';
          } else if (paveOption.values.includes('SP')) {
            this.selectedOptions.pave = 'SP';
          }
        }
      }
    }

    console.log('[GroupedProduct] Selected options:', this.selectedOptions);

    // Find matching product
    const handle = this.findProductHandle(this.selectedOptions);

    if (handle) {
      this.loadProduct(handle);
    } else {
      // No exact match - try to find closest available
      console.warn('[GroupedProduct] No product found for selection:', this.selectedOptions);
    }

    // Update button states
    this.updateOptionButtons();
  }

  updateOptionButtons() {
    const optionsContainer = document.getElementById('product-options');

    // Update option cards
    optionsContainer.querySelectorAll('.grouped-product__option-card').forEach(card => {
      const optionKey = card.dataset.option;
      const value = card.dataset.value;
      const isSelected = value === this.selectedOptions[optionKey];
      const isAvailable = this.isOptionAvailable(optionKey, value);

      card.classList.toggle('is-selected', isSelected);
      card.classList.toggle('is-unavailable', !isAvailable);
      card.disabled = !isAvailable;
    });

    // Update tab indicators to show which have selections
    optionsContainer.querySelectorAll('.grouped-product__tab').forEach(tab => {
      const optionKey = tab.dataset.tab;
      const hasSelection = this.selectedOptions[optionKey] !== undefined;
      tab.classList.toggle('has-selection', hasSelection);
    });
  }

  async loadProduct(handle) {
    // Show loading state
    this.setLoading(true);

    try {
      // Fetch product JSON from Shopify Ajax API
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) throw new Error('Product not found');

      const product = await response.json();

      // Update the display
      this.updateDisplay(product);

      // Update URL without reload (optional)
      this.updateUrl(handle);

    } catch (error) {
      console.error('Error loading product:', error);
    } finally {
      this.setLoading(false);
    }
  }

  updateDisplay(product) {
    // Update price
    const priceEl = document.getElementById('product-price');
    if (priceEl) {
      priceEl.textContent = this.formatMoney(product.price);
    }

    // Update images
    this.updateImages(product.images);

    // Update variant ID for cart
    const variantIdEl = document.getElementById('variant-id');
    if (variantIdEl && product.variants && product.variants.length > 0) {
      variantIdEl.value = product.variants[0].id;
    }

    // Update SKU
    const skuEl = document.getElementById('product-sku');
    if (skuEl && product.variants && product.variants.length > 0) {
      const sku = product.variants[0].sku;
      skuEl.innerHTML = sku ? `<span>SKU: ${sku}</span>` : '';
      console.log('[GroupedProduct] SKU updated to:', sku);
    } else {
      console.log('[GroupedProduct] SKU element not found or no variants');
    }

    // Update add to cart button
    const addBtn = document.getElementById('add-to-cart-btn');
    if (addBtn) {
      addBtn.disabled = !product.available;
      addBtn.textContent = product.available ? 'Add to Cart' : 'Sold Out';
    }

    // Store current product data
    this.currentProduct = {
      handle: product.handle,
      title: product.title,
      price: product.price,
      available: product.available,
      variantId: product.variants[0].id,
      sku: product.variants[0].sku,
      images: product.images
    };
  }

  updateImages(images) {
    const mainImage = document.getElementById('main-product-image');
    const thumbnailsContainer = document.getElementById('product-thumbnails');

    if (mainImage && images.length > 0) {
      mainImage.src = images[0];
      mainImage.alt = '';
    }

    if (thumbnailsContainer) {
      let html = '';
      images.forEach((imgUrl, index) => {
        html += `
          <button
            type="button"
            class="grouped-product__thumbnail ${index === 0 ? 'is-active' : ''}"
            data-image-url="${imgUrl}"
            data-index="${index}"
          >
            <img
              src="${imgUrl.replace(/(\.[^.]+)$/, '_150x150$1')}"
              alt=""
              loading="lazy"
              width="150"
              height="150"
            >
          </button>
        `;
      });
      thumbnailsContainer.innerHTML = html;

      // Rebind click events
      this.bindThumbnailClicks();
    }
  }

  bindThumbnailClicks() {
    const thumbnails = document.querySelectorAll('.grouped-product__thumbnail');
    thumbnails.forEach(thumb => {
      thumb.addEventListener('click', (e) => {
        const imgUrl = e.currentTarget.dataset.imageUrl;
        const mainImage = document.getElementById('main-product-image');

        if (mainImage) {
          mainImage.src = imgUrl;
        }

        // Update active state
        thumbnails.forEach(t => t.classList.remove('is-active'));
        e.currentTarget.classList.add('is-active');
      });
    });
  }

  bindFormSubmit() {
    const form = document.getElementById('grouped-product-form');
    if (!form) return;

    // Get the cart drawer element
    this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      // Infinite Options only alerts on empty required fields; it does not stop this custom add to cart
      const missingField = this.findMissingRequiredOption();
      if (missingField) {
        this.showRequiredOptionError(missingField);
        return;
      }

      // Validate ring size if present
      const ringSizeSelector = document.querySelector('.ring-size-selector');
      if (ringSizeSelector) {
        const ringSizeSelect = document.querySelector('#RingSize');
        const ringSizeRadio = document.querySelector('input[name="properties[Ring Size]"]:checked');
        const hasRingSize = (ringSizeSelect && ringSizeSelect.value) || ringSizeRadio;

        if (!hasRingSize) {
          const errorEl = document.getElementById('ring-size-error');
          if (errorEl) {
            errorEl.style.display = 'block';
            ringSizeSelector.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          return;
        }
      }

      const addBtn = document.getElementById('add-to-cart-btn');
      const spinner = addBtn?.querySelector('.loading__spinner');
      if (addBtn) {
        addBtn.classList.add('loading');
        addBtn.disabled = true;
        if (spinner) spinner.classList.remove('hidden');
      }

      try {
        const formData = new FormData(form);

        // Capture Ring Size if present
        const ringSizeSelect = document.querySelector('#RingSize');
        const ringSizeRadio = document.querySelector('input[name="properties[Ring Size]"]:checked');
        if (ringSizeSelect && ringSizeSelect.value) {
          formData.set('properties[Ring Size]', ringSizeSelect.value);
        } else if (ringSizeRadio && ringSizeRadio.value) {
          formData.set('properties[Ring Size]', ringSizeRadio.value);
        }

        // Capture Infinite Options fields
        const infiniteOptionsContainer = document.getElementById('infiniteoptions-container');
        if (infiniteOptionsContainer) {
          const infiniteOptionsFields = infiniteOptionsContainer.querySelectorAll('input[name^="properties"], select[name^="properties"], textarea[name^="properties"]');
          infiniteOptionsFields.forEach(field => {
            if (field.type === 'checkbox') {
              if (field.checked) {
                formData.set(field.name, field.value || 'Yes');
              }
            } else if (field.type === 'radio') {
              if (field.checked) {
                formData.set(field.name, field.value);
              }
            } else if (field.value) {
              formData.set(field.name, field.value);
            }
          });
        }

        // Add sections data for cart drawer update (same as theme's ProductForm)
        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          const sections = this.cart.getSectionsToRender().map(section => section.section);
          formData.append('sections', sections);
          formData.append('sections_url', window.location.pathname);
        }

        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/javascript'
          },
          body: formData
        });

        if (!response.ok) throw new Error('Failed to add to cart');

        const responseData = await response.json();

        // Check for errors in response
        if (responseData.status) {
          throw new Error(responseData.description || 'Failed to add to cart');
        }

        // Use the cart drawer's renderContents method to update and open the drawer
        if (this.cart && typeof this.cart.renderContents === 'function') {
          this.cart.renderContents(responseData);
        } else {
          // Fallback: show notification and update cart count
          this.showNotification('Added to cart!');
          this.updateCartCount();
        }

      } catch (error) {
        console.error('Error adding to cart:', error);
        this.showNotification(error.message || 'Error adding to cart', true);
      } finally {
        if (addBtn) {
          addBtn.classList.remove('loading');
          addBtn.disabled = false;
          if (spinner) spinner.classList.add('hidden');
        }
      }
    });
  }

  findMissingRequiredOption() {
    const container = document.getElementById('infiniteoptions-container');
    if (!container) return null;

    return Array.from(container.querySelectorAll('[required]')).find(field => {
      if (field.type === 'checkbox' || field.type === 'radio') {
        return !container.querySelector(`[name="${CSS.escape(field.name)}"]:checked`);
      }
      return !field.value;
    }) || null;
  }

  showRequiredOptionError(field) {
    const container = document.getElementById('infiniteoptions-container');
    const label = (field.name.match(/^properties\[(.+)\]$/) || [])[1] || 'option';

    let errorEl = container.querySelector('.grouped-product__required-error');
    if (!errorEl) {
      errorEl = document.createElement('p');
      errorEl.className = 'grouped-product__required-error';
      errorEl.setAttribute('role', 'alert');
      container.appendChild(errorEl);
    }
    errorEl.textContent = `Please choose your ${label.toLowerCase()}.`;
    errorEl.hidden = false;

    field.addEventListener('change', () => { errorEl.hidden = true; }, { once: true });
    field.scrollIntoView({ behavior: 'smooth', block: 'center' });
    field.focus({ preventScroll: true });
  }

  showNotification(message, isError = false) {
    const notification = document.createElement('div');
    notification.className = 'grouped-product__notification';
    notification.textContent = message;
    if (isError) {
      notification.style.background = '#c00';
    }
    document.body.appendChild(notification);

    setTimeout(() => {
      notification.remove();
    }, 3000);
  }

  updateCartCount() {
    // Fetch cart and update count in header
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        const countEl = document.querySelector('[data-cart-count]');
        if (countEl) {
          countEl.textContent = cart.item_count;
        }
      });
  }

  updateUrl(handle) {
    const newUrl = `/products/${handle}`;
    window.history.replaceState({}, '', newUrl);
  }

  setLoading(isLoading) {
    const gallery = document.querySelector('.grouped-product__main-image');
    if (gallery) {
      gallery.classList.toggle('is-loading', isLoading);
    }
  }

  formatMoney(cents) {
    // Basic money formatting - adjust for your currency
    return '$' + (cents / 100).toFixed(2);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new GroupedProductSelector());
} else {
  new GroupedProductSelector();
}
